/**
 * AuthBootstrap — runs the /auth/me fetch once on mount, then renders
 * children regardless of outcome. RoleGate handles the gating downstream
 * based on the populated (or still-null) adminUserAtom.
 *
 * The split keeps the bootstrap concern (network call timing, loading
 * shell, error handling) separate from the gating concern (which roles
 * see what). One refresh attempt per mount.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useAdminAuth } from './use-admin-auth.js'

interface AuthBootstrapProps {
  children: ReactNode
}

export function AuthBootstrap({ children }: AuthBootstrapProps): JSX.Element {
  const { user, refreshSession } = useAdminAuth()
  const [bootstrapped, setBootstrapped] = useState(false)

  useEffect(() => {
    if (user || bootstrapped) return
    let cancelled = false
    ;(async () => {
      try {
        await refreshSession()
      } catch {
        // A transient probe failure must not block direct sign-in. RoleGate
        // keeps protected data hidden; the login form reports submit failures.
      } finally {
        if (!cancelled) setBootstrapped(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user, bootstrapped, refreshSession])

  if (!user && !bootstrapped) {
    return (
      <main
        aria-label="loading"
        data-testid="auth-loading"
        className="min-h-screen flex items-center justify-center bg-surface-app"
      />
    )
  }

  return <>{children}</>
}
